import { zodResolver } from "@hookform/resolvers/zod";
import { Package } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import useApiRequest, { ApiResponse } from "@/lib/hooks/use-request";
import {
  CreateProductRequestDto,
  ProductCreationEntitiesResponseDto,
  ProductResponseDto,
  UpdateProductRequestDto,
} from "@/lib/interfaces/product";
import { Button } from "@/ui-components/ui/button";
import { Checkbox } from "@/ui-components/ui/checkbox";
import DataFetchParent from "@/ui-components/ui/data-fetch";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/ui-components/ui/form";
import { Input } from "@/ui-components/ui/input";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Textarea } from "@/ui-components/ui/textarea";
import { germanToBackendNumber } from "@/lib/hooks/utils";

const ProductEditor = ({
  item,
  onSubmitDone,
  withoutRedirect = false,
  initialText,
}: {
  item?: ProductResponseDto;
  onSubmitDone?: () => void;
  withoutRedirect?: boolean;
  initialText?: string;
}) => {
  const [creationEntities, setCreationEntities] = useState<ProductCreationEntitiesResponseDto | undefined>(
    item?.selectableEntities || undefined
  );
  const [isLoading, setIsLoading] = useState(false);
  const { apiRequest } = useApiRequest();

  const fetchCreationEntities = useCallback(async () => {
    setIsLoading(true);
    const res = await apiRequest<ProductCreationEntitiesResponseDto>("settings/products/creation-entities", "GET");
    setIsLoading(false);
    if (res.data) {
      setCreationEntities(res.data);
    }
  }, [apiRequest]);

  useEffect(() => {
    if (!creationEntities) {
      fetchCreationEntities();
    }
  }, [fetchCreationEntities, creationEntities]);

  return (
    <DataFetchParent
      data={creationEntities}
      renderElement={(data: ProductCreationEntitiesResponseDto) => (
        <ProductForm
          item={item}
          onSubmitDone={onSubmitDone}
          creationEntities={data}
          withoutRedirect={withoutRedirect}
          initialText={initialText}
        />
      )}
      isLoading={isLoading}
    />
  );
};

const ProductForm = ({
  item,
  onSubmitDone,
  creationEntities,
  withoutRedirect,
  initialText,
}: {
  item?: ProductResponseDto;
  onSubmitDone?: () => void;
  creationEntities: ProductCreationEntitiesResponseDto;
  withoutRedirect?: boolean;
  initialText?: string;
}) => {
  const { apiRequest } = useApiRequest();
  const navigate = useNavigate();

  const productSchema = z.object({
    productId: z.string().min(1, {
      message: "Produkt ID ist erforderlich",
    }),
    title: z
      .string()
      .min(1, {
        message: "Titel ist erforderlich",
      })
      .max(100, {
        message: "Titel darf maximal 100 Zeichen lang sein",
      }),
    description: z.string().optional(),
    netPrice: z
      .string()
      .min(1, {
        message: "Preis ist erforderlich",
      })
      .refine(
        (value) => /^(?:\d{1,3}(?:\.\d{3})*(?:,\d{1,2})?|\d+(?:,\d{1,2})?)$/.test(value),
        {
          message: "Ungültiges Zahlenformat. Beispiel: 2.000,50",
        }
      ),
    isBasicSecurity: z.boolean(),
    netInstallationCost: z
      .string()
      .min(1, {
        message: "Montagekosten sind erforderlich",
      })
      .refine(
        (value) => /^(?:\d{1,3}(?:\.\d{3})*(?:,\d{1,2})?|\d+(?:,\d{1,2})?)$/.test(value),
        {
          message: "Ungültiges Zahlenformat. Beispiel: 2.000,50",
        }
      ),
  });

  type ProductFormSchema = z.infer<typeof productSchema>;

  const form = useForm<ProductFormSchema>({
    resolver: zodResolver(productSchema),
    defaultValues: {
      productId: item?.productId || "",
      title: item?.title || initialText || "",
      description: item?.description || "",
      netPrice: item?.netPrice 
        ? item.netPrice.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : "",
      isBasicSecurity: item?.isBasicSecurity || false,
      netInstallationCost: item?.netInstallationCost 
        ? item.netInstallationCost.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : "",
    },
  });

  const onSubmit = async (data: ProductFormSchema) => {
    let res: ApiResponse<ProductResponseDto>;
    if (item) {
      const req: UpdateProductRequestDto = {
        productId: data.productId !== item.productId ? data.productId : undefined,
        title: data.title !== item.title ? data.title : undefined,
        description: data.description !== item.description ? data.description : undefined,
        netPrice: data.netPrice.toString() !== item.netPrice.toString() ? germanToBackendNumber(data.netPrice).toString() : undefined,
        isBasicSecurity: data.isBasicSecurity !== item.isBasicSecurity ? data.isBasicSecurity : undefined,
        netInstallationCost: data.netInstallationCost.toString() !== item.netInstallationCost.toString() ? germanToBackendNumber(data.netInstallationCost).toString() : undefined,
      };

      res = await apiRequest<ProductResponseDto>(`settings/products/${item._id}`, "PATCH", {
        body: req,
        toast: { toastText: "Produkt erfolgreich aktualisiert" },
      });
    } else {
      const req: CreateProductRequestDto = {
        productId: data.productId,
        title: data.title,
        description: data.description,
        netPrice: germanToBackendNumber(data.netPrice).toString(),
        isBasicSecurity: data.isBasicSecurity,
        netInstallationCost: germanToBackendNumber(data.netInstallationCost).toString(),
      };

      res = await apiRequest("settings/products", "POST", {
        body: req,
        toast: { toastText: "Produkt erfolgreich erstellt" },
      });
    }
    if (res.data) {
      if (onSubmitDone) {
        onSubmitDone();
      }
      if (!withoutRedirect) {
        navigate(`/settings/products/${res.data._id}`);
      }
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-5 h-full">
        <div className="flex flex-col gap-2">
          <FormLabel className="text-[16px] font-bold">Produkt ID:*</FormLabel>
          <FormField
            control={form.control}
            name="productId"
            render={({ field }) => (
              <FormItem className="flex flex-col bg-white rounded-lg">
                <FormControl>
                  <Input {...field} placeholder="Produkt ID" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="flex flex-col gap-2">
          <FormLabel className="text-[16px] font-bold">Produkt Titel:*</FormLabel>
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem className="flex flex-col bg-white rounded-lg">
                <FormControl>
                  <Input {...field} placeholder="Produkt Titel" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="flex flex-col gap-2">
          <FormLabel className="text-[16px] font-bold">Produkt Beschreibung</FormLabel>
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem className="flex flex-col bg-white rounded-lg">
                <FormControl>
                  <Textarea 
                    {...field} 
                    placeholder="Produkt Beschreibung"
                    value={field.value || ''}
                    className="min-h-[100px]"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="flex flex-col gap-2">
          <FormLabel className="text-[16px] font-bold">Materialkosten (Netto):*</FormLabel>
          <FormField
            control={form.control}
            name="netPrice"
            render={({ field }) => (
              <FormItem className="flex flex-col bg-white rounded-lg">
                <FormControl>
                  <Input {...field} placeholder="Materialkosten" type="text" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="flex flex-col gap-2">
          <FormLabel className="text-[16px] font-bold">Montagekosten (Netto):*</FormLabel>
          <FormField
            control={form.control}
            name="netInstallationCost"
            render={({ field }) => (
              <FormItem className="flex flex-col bg-white rounded-lg">
                <FormControl>
                  <Input {...field} placeholder="Montagekosten" type="text" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="flex flex-col gap-2">
          <FormLabel className="text-[16px] font-bold">Provision nach Vertriebsrang (Grundabsicherung)</FormLabel>
          <FormField
            control={form.control}
            name="isBasicSecurity"
            render={({ field }) => (
              <FormItem className="flex flex-col bg-white rounded-lg">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={!creationEntities.basicSecurityAllowed && !item?.isBasicSecurity}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <Button type="submit" variant="default" className="text-white font-bold flex items-center gap-4 w-fit self-end">
          <Package /> <span>{item ? "Produkt bearbeiten" : "Produkt erstellen"}</span>
        </Button>
      </form>
    </Form>
  );
};

export default ProductEditor;
